import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api, { Session, Comment, CommentStatus, SessionCommentSummary } from '../services/api';
import StatusBanner from '../components/StatusBanner';
import AuroraBackground from '../components/AuroraBackground';
import { encodeAccountId } from '../services/common';
import { useSessionSubscription } from '../hooks/useSessionSubscription';
import { useAuth } from '../contexts/AuthContext';
import { useUserData } from '../contexts/UserDataContext';

const HEADER_HEIGHT = 64;

function deriveCommentStatus(c: SessionCommentSummary): CommentStatus {
    if (c.posted || c.selected) return 'accepted';
    if (c.declined) return 'declined';
    return 'underReview';
}

const SessionPage: React.FC = () => {
    const { sessionId } = useParams<{ sessionId: string }>();
    const navigate = useNavigate();
    const hasSyncedRef = useRef(false);
    const { user } = useAuth();
    const canAccessQueue = !!user && (user.creditBalance ?? 0) > 0;

    const { findSessionInContext } = useUserData();

    const [isAutoPostingFeatureActivated, setIsAutoPostingFeatureActivated] = useState(true);

    useEffect(() => {
        api.getFrontendConfig().then((config) => {
            setIsAutoPostingFeatureActivated(config.isAutoPostingFeatureActivated);
        }).catch((err) => {
            console.error('Failed to fetch frontend config:', err);
        });
    }, []);

    // Track pending optimistic updates (comment id -> updated fields)
    const [optimisticUpdates, setOptimisticUpdates] = useState<Map<string, Partial<Comment>>>(new Map());

    // Look up cached data from UserContext for instant display
    const cachedData = useMemo(() => {
        if (!sessionId) return null;
        return findSessionInContext(sessionId);
    }, [sessionId, findSessionInContext]);

    // Build initialData for the subscription hook from cache
    const initialData = useMemo(() => {
        if (!cachedData) return undefined;
        const { session: cachedSession, comments: cachedComments, accountUrl: cachedAccountUrl } = cachedData;

        // Convert SessionWithDetails → Session
        const sessionSeed: Session = {
            id: cachedSession.id,
            status: cachedSession.status || 'generating',
            accountUrl: cachedAccountUrl,
            progress: cachedSession.progress,
            createdAt: cachedSession.createdAt,
        };

        // Convert SessionCommentSummary[] → Comment[]
        const commentsSeed: Comment[] = cachedComments.map(c => ({
            id: c.id,
            postUrl: c.postUrl,
            username: c.username,
            content: c.content,
            imageUrl: c.imageUrl,
            rationale: c.rationale,
            status: deriveCommentStatus(c),
            selected: c.selected,
            declined: c.declined,
            posted: c.posted,
            createdAt: c.createdAt,
        }));

        return { session: sessionSeed, comments: commentsSeed };
    }, [cachedData]);

    // accountUrl: prefer cache (synchronous), fall back to API-fetched value.
    // Track the API-fetched value separately and reset it when sessionId changes
    // to prevent stale accountUrl from a previous session reaching the hook.
    const [fetchedAccountUrl, setFetchedAccountUrl] = useState<string | undefined>(undefined);
    const prevSessionIdRef = useRef(sessionId);
    if (prevSessionIdRef.current !== sessionId) {
        prevSessionIdRef.current = sessionId;
        // Reset on session change so the hook doesn't see a stale URL
        if (fetchedAccountUrl !== undefined) {
            setFetchedAccountUrl(undefined);
        }
    }
    const accountUrl = cachedData?.accountUrl ?? fetchedAccountUrl;

    // Use the real-time subscription hook
    const {
        session: realtimeSession,
        comments: realtimeComments,
        loading,
        error,
        totalCommentCount,
        hasStartedReviewing,
    } = useSessionSubscription(sessionId, accountUrl, { initialData });

    // Local session state for optimistic updates
    const [sessionOverride, setSessionOverride] = useState<Partial<Session> | null>(null);

    // Merge session with optimistic overrides
    const session = realtimeSession
        ? { ...realtimeSession, ...sessionOverride }
        : null;

    // Merge comments with optimistic updates
    const comments = realtimeComments.map((comment) => {
        const updates = optimisticUpdates.get(comment.id);
        return updates ? { ...comment, ...updates } : comment;
    });

    // Snap scroll state
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const [currentIndex, setCurrentIndex] = useState(0);

    // Editing state
    const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
    const [editedContent, setEditedContent] = useState('');

    // Fetch accountUrl via API when not available from cache
    useEffect(() => {
        if (!sessionId) return;
        // Skip API call if cache already provides accountUrl
        if (cachedData?.accountUrl) return;

        api.getSession(sessionId).then((sessionData) => {
            setFetchedAccountUrl(sessionData.accountUrl);
        }).catch((err) => {
            console.error('[SessionPage] Failed to fetch initial session:', err);
        });
    }, [sessionId, cachedData?.accountUrl]);

    // Clear optimistic updates when realtime data confirms them
    useEffect(() => {
        if (realtimeComments.length > 0 && optimisticUpdates.size > 0) {
            const newOptimistic = new Map(optimisticUpdates);
            optimisticUpdates.forEach((updates, commentId) => {
                const realtimeComment = realtimeComments.find(c => c.id === commentId);
                if (realtimeComment) {
                    const allConfirmed = Object.entries(updates).every(
                        ([key, value]) => realtimeComment[key as keyof Comment] === value
                    );
                    if (allConfirmed) {
                        newOptimistic.delete(commentId);
                    }
                }
            });
            if (newOptimistic.size !== optimisticUpdates.size) {
                setOptimisticUpdates(newOptimistic);
            }
        }
    }, [realtimeComments, optimisticUpdates]);

    // Clear session override when realtime data confirms it
    useEffect(() => {
        if (realtimeSession && sessionOverride) {
            const allConfirmed = Object.entries(sessionOverride).every(
                ([key, value]) => realtimeSession[key as keyof Session] === value
            );
            if (allConfirmed) {
                setSessionOverride(null);
            }
        }
    }, [realtimeSession, sessionOverride]);

    // Sync selected comments to posting queue on first load (fire and forget)
    useEffect(() => {
        if (session?.accountUrl && !hasSyncedRef.current) {
            hasSyncedRef.current = true;
            const accountId = encodeAccountId(session.accountUrl);
            api.syncPostingQueue(accountId).catch((err) => {
                console.error('Sync failed (non-blocking):', err);
            });
        }
    }, [session?.accountUrl]);

    // Track current slide via IntersectionObserver
    useEffect(() => {
        const container = scrollContainerRef.current;
        if (!container || comments.length === 0) return;

        const observer = new IntersectionObserver(
            (entries) => {
                for (const entry of entries) {
                    if (entry.isIntersecting) {
                        const idx = Number(entry.target.getAttribute('data-index'));
                        if (!isNaN(idx)) {
                            setCurrentIndex(idx);
                        }
                    }
                }
            },
            {
                root: container,
                threshold: 0.6,
            }
        );

        const slides = container.querySelectorAll('[data-index]');
        slides.forEach((slide) => observer.observe(slide));

        return () => observer.disconnect();
    }, [comments.length]);

    // Keyboard navigation
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            const container = scrollContainerRef.current;
            if (!container || comments.length === 0) return;

            // Don't intercept if user is editing
            if (editingCommentId) return;

            if (e.key === 'ArrowDown' || e.key === 'PageDown') {
                e.preventDefault();
                const nextIdx = Math.min(currentIndex + 1, comments.length - 1);
                const nextSlide = container.querySelector(`[data-index="${nextIdx}"]`);
                if (nextSlide) nextSlide.scrollIntoView({ behavior: 'auto' });
            } else if (e.key === 'ArrowUp' || e.key === 'PageUp') {
                e.preventDefault();
                const prevIdx = Math.max(currentIndex - 1, 0);
                const prevSlide = container.querySelector(`[data-index="${prevIdx}"]`);
                if (prevSlide) prevSlide.scrollIntoView({ behavior: 'auto' });
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [currentIndex, comments.length, editingCommentId]);

    const handleAccept = async (id: string) => {
        if (!sessionId || !session) return;

        setOptimisticUpdates((prev) => {
            const next = new Map(prev);
            next.set(id, { status: 'accepted' as const, selected: true });
            return next;
        });

        try {
            const result = await api.updateComments(sessionId, [{ id, selected: true }]);
            if (result.status === 'completed') {
                setSessionOverride({ status: 'completed' });
            }
            const accountId = encodeAccountId(session.accountUrl);
            api.syncPostingQueue(accountId).catch(err =>
                console.error('Queue sync failed (non-blocking):', err)
            );
        } catch (err) {
            console.error('Failed to accept comment:', err);
            setOptimisticUpdates((prev) => {
                const next = new Map(prev);
                next.delete(id);
                return next;
            });
        }
    };

    const handleDecline = async (id: string) => {
        if (!sessionId) return;

        setOptimisticUpdates((prev) => {
            const next = new Map(prev);
            next.set(id, { status: 'declined' as const, selected: false, declined: true });
            return next;
        });

        try {
            const result = await api.updateComments(sessionId, [{ id, selected: false, declined: true }]);
            if (result.status === 'completed' && session) {
                setSessionOverride({ status: 'completed' });
            }
        } catch (err) {
            console.error('Failed to decline comment:', err);
            setOptimisticUpdates((prev) => {
                const next = new Map(prev);
                next.delete(id);
                return next;
            });
        }
    };

    const handleEditComment = async (id: string, content: string) => {
        if (!sessionId) return;

        setOptimisticUpdates((prev) => {
            const next = new Map(prev);
            next.set(id, { ...prev.get(id), content });
            return next;
        });

        try {
            await api.updateComments(sessionId, [{ id, content }]);
        } catch (err) {
            console.error('Failed to update comment:', err);
        }
    };

    const handleAcceptAll = async () => {
        if (!sessionId || !session) return;

        const pendingComments = comments.filter((c) => !c.posted && c.status === 'underReview');
        const updates = pendingComments.map((c) => ({ id: c.id, selected: true }));

        setOptimisticUpdates((prev) => {
            const next = new Map(prev);
            for (const comment of pendingComments) {
                next.set(comment.id, { status: 'accepted' as const, selected: true });
            }
            return next;
        });

        try {
            const result = await api.updateComments(sessionId, updates);
            if (result.status === 'completed') {
                setSessionOverride({ status: 'completed' });
            }
            const accountId = encodeAccountId(session.accountUrl);
            api.syncPostingQueue(accountId).catch(err =>
                console.error('Queue sync failed (non-blocking):', err)
            );
        } catch (err) {
            console.error('Failed to accept all:', err);
            setOptimisticUpdates((prev) => {
                const next = new Map(prev);
                for (const comment of pendingComments) {
                    next.delete(comment.id);
                }
                return next;
            });
        }
    };

    const handleGoToQueue = () => {
        if (!session) return;
        const accountId = encodeAccountId(session.accountUrl);
        navigate(`/queue/${accountId}`);
    };

    const handleDeleteSession = async () => {
        if (!sessionId) return;

        if (!window.confirm('Are you sure you want to delete this session? This action cannot be undone.')) {
            return;
        }

        try {
            await api.deleteSession(sessionId);
            navigate('/');
        } catch (err) {
            alert(err instanceof Error ? err.message : 'Failed to delete session');
        }
    };

    const startEditing = useCallback((comment: Comment) => {
        setEditingCommentId(comment.id);
        setEditedContent(comment.content);
    }, []);

    const saveEdit = useCallback(() => {
        if (editingCommentId) {
            handleEditComment(editingCommentId, editedContent);
            setEditingCommentId(null);
        }
    }, [editingCommentId, editedContent]);

    const cancelEdit = useCallback(() => {
        setEditingCommentId(null);
        setEditedContent('');
    }, []);

    // Scroll to next comment after action
    const scrollToNext = useCallback(() => {
        const container = scrollContainerRef.current;
        if (!container) return;
        const nextIdx = currentIndex + 1;
        if (nextIdx < comments.length) {
            const nextSlide = container.querySelector(`[data-index="${nextIdx}"]`);
            if (nextSlide) {
                setTimeout(() => nextSlide.scrollIntoView({ behavior: 'auto' }), 200);
            }
        }
    }, [currentIndex, comments.length]);

    const handleAcceptAndScroll = useCallback((id: string) => {
        handleAccept(id);
        scrollToNext();
    }, [handleAccept, scrollToNext]);

    const handleDeclineAndScroll = useCallback((id: string) => {
        handleDecline(id);
        scrollToNext();
    }, [handleDecline, scrollToNext]);

    if (loading) {
        return (
            <>
                <AuroraBackground />
                <div style={{
                    minHeight: '100vh',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", sans-serif',
                }}>
                    <div style={{ textAlign: 'center' }}>
                        <div style={{
                            width: '40px',
                            height: '40px',
                            border: '2px solid rgba(0, 0, 0, 0.1)',
                            borderTop: '2px solid #7C3AED',
                            borderRadius: '50%',
                            animation: 'spin 1s linear infinite',
                            margin: '0 auto 16px',
                        }} />
                        <p style={{ color: '#666666', fontSize: '15px' }}>Loading session...</p>
                    </div>
                    <style>
                        {`
                            @keyframes spin {
                                0% { transform: rotate(0deg); }
                                100% { transform: rotate(360deg); }
                            }
                        `}
                    </style>
                </div>
            </>
        );
    }

    if (error || !session) {
        return (
            <>
                <AuroraBackground />
                <div style={{
                    minHeight: '100vh',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", sans-serif',
                    padding: '20px',
                }}>
                    <div style={{
                        textAlign: 'center',
                        padding: '48px',
                        maxWidth: '400px',
                        background: 'rgba(255, 255, 255, 0.9)',
                        backdropFilter: 'blur(10px)',
                        borderRadius: '16px',
                        border: '1px solid rgba(0, 0, 0, 0.1)',
                    }}>
                        <div style={{
                            width: '64px',
                            height: '64px',
                            borderRadius: '50%',
                            background: 'rgba(124, 58, 237, 0.1)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            margin: '0 auto 24px',
                            color: '#7C3AED',
                        }}>
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                <circle cx="12" cy="12" r="10" />
                                <line x1="12" y1="8" x2="12" y2="12" />
                                <line x1="12" y1="16" x2="12.01" y2="16" />
                            </svg>
                        </div>
                        <h2 style={{ fontSize: '24px', fontWeight: 500, color: '#000000', marginBottom: '12px' }}>
                            Session Not Found
                        </h2>
                        <p style={{ color: '#666666', marginBottom: '32px', fontSize: '15px' }}>
                            {error || 'This session does not exist or has been deleted.'}
                        </p>
                        <button
                            onClick={() => navigate('/')}
                            style={{
                                padding: '14px 28px',
                                background: '#7C3AED',
                                color: '#FFFFFF',
                                border: 'none',
                                borderRadius: '4px',
                                fontSize: '15px',
                                fontWeight: 600,
                                cursor: 'pointer',
                            }}
                        >
                            Go Back Home
                        </button>
                    </div>
                </div>
            </>
        );
    }

    const selectedCount = comments.filter((c) => c.status === 'accepted' && !c.posted).length;
    const postedCount = comments.filter((c) => c.posted).length;
    const pendingCount = comments.filter((c) => c.status === 'underReview' && !c.posted).length;
    const isEditable = ['awaiting_selection', 'completed', 'generating', 'initializing'].includes(session.status);
    const canPost = selectedCount > 0 && ['awaiting_selection', 'completed', 'needs_login'].includes(session.status);
    const username = session.accountUrl.replace(/^https?:\/\/(www\.)?instagram\.com\//, '').replace(/\/$/, '');

    const getStatusBadge = (comment: Comment) => {
        if (comment.posted) {
            return { label: 'Posted', bg: '#BBF7D0', color: '#166534' };
        }
        if (comment.status === 'accepted') {
            return { label: 'Accepted', bg: '#7C3AED', color: '#FFFFFF' };
        }
        if (comment.status === 'declined') {
            return { label: 'Declined', bg: '#FEE2E2', color: '#991B1B' };
        }
        return { label: 'Pending', bg: '#FEF3C7', color: '#92400E' };
    };

    const hasComments = comments.length > 0 || (hasStartedReviewing && totalCommentCount > 0);

    return (
        <>
            <AuroraBackground />
            <div style={{
                height: '100vh',
                display: 'flex',
                flexDirection: 'column',
                fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", sans-serif',
                overflow: 'hidden',
            }}>
                {/* Compact Header Bar */}
                <div style={{
                    height: `${HEADER_HEIGHT}px`,
                    minHeight: `${HEADER_HEIGHT}px`,
                    background: 'rgba(255, 255, 255, 0.95)',
                    backdropFilter: 'blur(10px)',
                    borderBottom: '1px solid rgba(0, 0, 0, 0.1)',
                    display: 'flex',
                    alignItems: 'center',
                    padding: '0 20px',
                    gap: '12px',
                    zIndex: 10,
                }}>
                    {/* Back button */}
                    <button
                        onClick={() => navigate('/')}
                        style={{
                            background: 'transparent',
                            border: 'none',
                            cursor: 'pointer',
                            padding: '6px',
                            color: '#666666',
                            display: 'flex',
                            alignItems: 'center',
                            flexShrink: 0,
                        }}
                        title="Back to Home"
                    >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M19 12H5M12 19l-7-7 7-7" />
                        </svg>
                    </button>

                    {/* Username + profile link */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
                        <span style={{ fontSize: '16px', fontWeight: 600, color: '#000000' }}>
                            @{username}
                        </span>
                        <a
                            href={session.accountUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: '#7C3AED', display: 'flex', alignItems: 'center' }}
                            title="View Instagram Profile"
                        >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                                <polyline points="15,3 21,3 21,9" />
                                <line x1="10" y1="14" x2="21" y2="3" />
                            </svg>
                        </a>
                    </div>

                    {/* Status Banner (inline/compact) */}
                    <div style={{ flexShrink: 0, maxWidth: '300px' }}>
                        <StatusBanner
                            status={session.status}
                            vncUrl={session.vncUrl}
                            progress={session.progress}
                            error={session.error}
                            hideVncLink={!isAutoPostingFeatureActivated}
                        />
                    </div>

                    {/* Stats badges */}
                    {hasComments && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
                            <span style={{
                                fontSize: '12px',
                                fontWeight: 600,
                                padding: '3px 10px',
                                borderRadius: '12px',
                                background: 'rgba(124, 58, 237, 0.1)',
                                color: '#7C3AED',
                            }}>
                                {['generating', 'initializing'].includes(session.status)
                                    ? `${comments.length}/${totalCommentCount}`
                                    : comments.length} total
                            </span>
                            <span style={{
                                fontSize: '12px',
                                fontWeight: 600,
                                padding: '3px 10px',
                                borderRadius: '12px',
                                background: 'rgba(16, 185, 129, 0.1)',
                                color: '#10B981',
                            }}>
                                {selectedCount} accepted
                            </span>
                            <span style={{
                                fontSize: '12px',
                                fontWeight: 600,
                                padding: '3px 10px',
                                borderRadius: '12px',
                                background: 'rgba(245, 158, 11, 0.1)',
                                color: '#F59E0B',
                            }}>
                                {pendingCount} pending
                            </span>
                            {postedCount > 0 && (
                                <span style={{
                                    fontSize: '12px',
                                    fontWeight: 600,
                                    padding: '3px 10px',
                                    borderRadius: '12px',
                                    background: 'rgba(59, 130, 246, 0.1)',
                                    color: '#3B82F6',
                                }}>
                                    {postedCount} posted
                                </span>
                            )}
                        </div>
                    )}

                    {/* Spacer */}
                    <div style={{ flex: 1 }} />

                    {/* Action buttons */}
                    {(isEditable || canPost) && comments.length > 0 && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
                            <button
                                onClick={handleAcceptAll}
                                disabled={pendingCount === 0}
                                style={{
                                    padding: '6px 14px',
                                    background: pendingCount > 0 ? 'rgba(124, 58, 237, 0.1)' : '#F5F5F5',
                                    color: pendingCount > 0 ? '#7C3AED' : '#999999',
                                    border: pendingCount > 0 ? '1px solid rgba(124, 58, 237, 0.3)' : '1px solid #E5E5E5',
                                    borderRadius: '4px',
                                    fontSize: '13px',
                                    fontWeight: 500,
                                    cursor: pendingCount > 0 ? 'pointer' : 'not-allowed',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <polyline points="20,6 9,17 4,12" />
                                </svg>
                                Accept All
                            </button>

                            {canAccessQueue && (
                                <button
                                    onClick={handleGoToQueue}
                                    style={{
                                        padding: '6px 14px',
                                        background: '#7C3AED',
                                        color: '#FFFFFF',
                                        border: 'none',
                                        borderRadius: '4px',
                                        fontSize: '13px',
                                        fontWeight: 600,
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '6px',
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                        <line x1="22" y1="2" x2="11" y2="13" />
                                        <polygon points="22,2 15,22 11,13 2,9 22,2" />
                                    </svg>
                                    Queue
                                </button>
                            )}
                        </div>
                    )}

                    {/* Sign-up link for anonymous users */}
                    {!canAccessQueue && comments.length > 0 && (
                        <Link
                            to="/login"
                            style={{
                                padding: '6px 14px',
                                background: '#7C3AED',
                                color: '#FFFFFF',
                                borderRadius: '4px',
                                fontSize: '13px',
                                fontWeight: 600,
                                textDecoration: 'none',
                                whiteSpace: 'nowrap',
                                flexShrink: 0,
                            }}
                        >
                            Sign Up to Post
                        </Link>
                    )}

                    {/* Delete button */}
                    <button
                        onClick={handleDeleteSession}
                        style={{
                            background: 'transparent',
                            border: 'none',
                            cursor: 'pointer',
                            padding: '6px',
                            color: '#EF4444',
                            display: 'flex',
                            alignItems: 'center',
                            flexShrink: 0,
                        }}
                        title="Delete session"
                    >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                            <polyline points="3,6 5,6 21,6" />
                            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                        </svg>
                    </button>
                </div>

                {/* Main Content Area - Snap Scroll */}
                {comments.length === 0 && !hasStartedReviewing ? (
                    <div style={{
                        flex: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}>
                        <div style={{
                            textAlign: 'center',
                            padding: '80px 40px',
                            background: 'rgba(255, 255, 255, 0.9)',
                            backdropFilter: 'blur(10px)',
                            borderRadius: '8px',
                            border: '1px solid rgba(0, 0, 0, 0.1)',
                            maxWidth: '480px',
                        }}>
                            {['initializing', 'generating'].includes(session.status) ? (
                                <>
                                    <div style={{
                                        width: '64px',
                                        height: '64px',
                                        borderRadius: '50%',
                                        background: 'rgba(124, 58, 237, 0.1)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        margin: '0 auto 24px',
                                        color: '#7C3AED',
                                    }}>
                                        <div style={{
                                            width: '24px',
                                            height: '24px',
                                            border: '2px solid rgba(124, 58, 237, 0.3)',
                                            borderTop: '2px solid #7C3AED',
                                            borderRadius: '50%',
                                            animation: 'spin 1s linear infinite',
                                        }} />
                                    </div>
                                    <h3 style={{ fontSize: '18px', fontWeight: 500, color: '#000000', marginBottom: '8px' }}>
                                        AI is working
                                    </h3>
                                    <p style={{ color: '#666666', fontSize: '15px' }}>
                                        Analyzing the account and generating personalized comments.
                                    </p>
                                </>
                            ) : (
                                <>
                                    <div style={{
                                        width: '64px',
                                        height: '64px',
                                        borderRadius: '50%',
                                        background: 'rgba(0, 0, 0, 0.05)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        margin: '0 auto 24px',
                                        color: '#999999',
                                    }}>
                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                                        </svg>
                                    </div>
                                    <h3 style={{ fontSize: '18px', fontWeight: 500, color: '#000000', marginBottom: '8px' }}>
                                        No comments available
                                    </h3>
                                    <p style={{ color: '#666666', fontSize: '15px' }}>
                                        No comments were generated for this session.
                                    </p>
                                </>
                            )}
                        </div>
                    </div>
                ) : (
                    <div
                        ref={scrollContainerRef}
                        style={{
                            flex: 1,
                            overflowY: 'auto',
                            scrollSnapType: 'y mandatory',
                            scrollBehavior: 'smooth',
                        }}
                    >
                        {comments.map((comment, index) => {
                            const badge = getStatusBadge(comment);
                            const isEditingThis = editingCommentId === comment.id;

                            return (
                                <div
                                    key={comment.id}
                                    data-index={index}
                                    style={{
                                        height: `calc(100vh - ${HEADER_HEIGHT}px)`,
                                        scrollSnapAlign: 'start',
                                        display: 'flex',
                                        padding: '16px',
                                        gap: '16px',
                                        boxSizing: 'border-box',
                                    }}
                                >
                                    {/* Left Panel - Post Image + Caption */}
                                    <div style={{
                                        width: '45%',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        background: 'rgba(255, 255, 255, 0.95)',
                                        backdropFilter: 'blur(10px)',
                                        borderRadius: '12px',
                                        border: '1px solid rgba(0, 0, 0, 0.1)',
                                        overflow: 'hidden',
                                    }}>
                                        <div style={{
                                            flex: 1,
                                            overflowY: 'auto',
                                            padding: '20px',
                                            display: 'flex',
                                            flexDirection: 'column',
                                        }}>
                                            {/* Post Image */}
                                            {comment.imageUrl && (
                                                <div style={{
                                                    borderRadius: '8px',
                                                    overflow: 'hidden',
                                                    marginBottom: '16px',
                                                    flexShrink: 0,
                                                }}>
                                                    <img
                                                        src={comment.imageUrl}
                                                        alt="Post"
                                                        style={{
                                                            width: '100%',
                                                            maxHeight: '55vh',
                                                            objectFit: 'contain',
                                                            display: 'block',
                                                            background: '#F7F7F7',
                                                        }}
                                                    />
                                                </div>
                                            )}

                                            {/* Post Caption */}
                                            {comment.caption && (
                                                <div style={{
                                                    padding: '12px 16px',
                                                    background: '#F7F7F7',
                                                    borderRadius: '8px',
                                                    marginBottom: '16px',
                                                    flexShrink: 0,
                                                }}>
                                                    <div style={{
                                                        fontSize: '11px',
                                                        fontWeight: 600,
                                                        color: '#999999',
                                                        textTransform: 'uppercase',
                                                        letterSpacing: '0.05em',
                                                        marginBottom: '6px',
                                                    }}>
                                                        Original Caption
                                                    </div>
                                                    <p style={{
                                                        fontSize: '14px',
                                                        color: '#333333',
                                                        lineHeight: '1.5',
                                                        margin: 0,
                                                        whiteSpace: 'pre-wrap',
                                                        wordBreak: 'break-word',
                                                    }}>
                                                        {comment.caption}
                                                    </p>
                                                </div>
                                            )}

                                            {/* View post link */}
                                            <a
                                                href={comment.postUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                style={{
                                                    fontSize: '13px',
                                                    color: '#7C3AED',
                                                    textDecoration: 'none',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '6px',
                                                    flexShrink: 0,
                                                }}
                                            >
                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                                    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                                                    <polyline points="15,3 21,3 21,9" />
                                                    <line x1="10" y1="14" x2="21" y2="3" />
                                                </svg>
                                                View original post
                                            </a>

                                            {/* Spacer to push nav indicator to bottom */}
                                            <div style={{ flex: 1 }} />

                                            {/* Navigation indicator */}
                                            <div style={{
                                                textAlign: 'center',
                                                paddingTop: '16px',
                                                fontSize: '13px',
                                                color: '#999999',
                                                fontWeight: 500,
                                            }}>
                                                {index + 1} / {comments.length}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Right Panel - Comment + Rationale + Actions */}
                                    <div style={{
                                        width: '55%',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        background: 'rgba(255, 255, 255, 0.95)',
                                        backdropFilter: 'blur(10px)',
                                        borderRadius: '12px',
                                        border: '1px solid rgba(0, 0, 0, 0.1)',
                                        overflow: 'hidden',
                                    }}>
                                        <div style={{
                                            flex: 1,
                                            overflowY: 'auto',
                                            padding: '20px',
                                            display: 'flex',
                                            flexDirection: 'column',
                                        }}>
                                            {/* Username + Status Badge */}
                                            <div style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '10px',
                                                marginBottom: '20px',
                                                flexShrink: 0,
                                            }}>
                                                <span style={{
                                                    fontWeight: 600,
                                                    fontSize: '16px',
                                                    color: '#000000',
                                                }}>
                                                    @{comment.username || 'unknown'}
                                                </span>
                                                <span style={{
                                                    padding: '3px 10px',
                                                    borderRadius: '4px',
                                                    fontSize: '11px',
                                                    fontWeight: 600,
                                                    backgroundColor: badge.bg,
                                                    color: badge.color,
                                                    textTransform: 'uppercase',
                                                    letterSpacing: '0.05em',
                                                }}>
                                                    {badge.label}
                                                </span>
                                            </div>

                                            {/* Generated Comment */}
                                            <div style={{ marginBottom: '16px', flexShrink: 0 }}>
                                                <div style={{
                                                    fontSize: '11px',
                                                    fontWeight: 600,
                                                    color: '#999999',
                                                    textTransform: 'uppercase',
                                                    letterSpacing: '0.05em',
                                                    marginBottom: '8px',
                                                }}>
                                                    Generated Comment
                                                </div>

                                                {isEditingThis ? (
                                                    <div>
                                                        <textarea
                                                            value={editedContent}
                                                            onChange={(e) => setEditedContent(e.target.value)}
                                                            style={{
                                                                width: '100%',
                                                                minHeight: '120px',
                                                                padding: '16px',
                                                                border: '2px solid #7C3AED',
                                                                borderRadius: '8px',
                                                                fontSize: '15px',
                                                                lineHeight: '1.6',
                                                                resize: 'vertical',
                                                                fontFamily: 'inherit',
                                                                outline: 'none',
                                                                boxSizing: 'border-box',
                                                            }}
                                                        />
                                                        <div style={{ marginTop: '8px', display: 'flex', gap: '8px' }}>
                                                            <button
                                                                onClick={saveEdit}
                                                                style={{
                                                                    padding: '8px 16px',
                                                                    background: '#7C3AED',
                                                                    color: '#FFFFFF',
                                                                    border: 'none',
                                                                    borderRadius: '4px',
                                                                    fontSize: '13px',
                                                                    fontWeight: 600,
                                                                    cursor: 'pointer',
                                                                }}
                                                            >
                                                                Save
                                                            </button>
                                                            <button
                                                                onClick={cancelEdit}
                                                                style={{
                                                                    padding: '8px 16px',
                                                                    background: '#F7F7F7',
                                                                    color: '#333333',
                                                                    border: '1px solid #E5E5E5',
                                                                    borderRadius: '4px',
                                                                    fontSize: '13px',
                                                                    fontWeight: 500,
                                                                    cursor: 'pointer',
                                                                }}
                                                            >
                                                                Cancel
                                                            </button>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div>
                                                        <p style={{
                                                            fontSize: '16px',
                                                            color: '#333333',
                                                            lineHeight: '1.7',
                                                            margin: 0,
                                                            padding: '16px',
                                                            backgroundColor: '#F7F7F7',
                                                            borderRadius: '8px',
                                                        }}>
                                                            &ldquo;{comment.content}&rdquo;
                                                        </p>
                                                        {!comment.posted && isEditable && (
                                                            <button
                                                                onClick={() => startEditing(comment)}
                                                                style={{
                                                                    marginTop: '8px',
                                                                    padding: '6px 12px',
                                                                    backgroundColor: 'transparent',
                                                                    color: '#666666',
                                                                    border: '1px solid #E5E5E5',
                                                                    borderRadius: '4px',
                                                                    fontSize: '12px',
                                                                    fontWeight: 500,
                                                                    cursor: 'pointer',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    gap: '4px',
                                                                }}
                                                            >
                                                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                                                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                                                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                                                                </svg>
                                                                Edit
                                                            </button>
                                                        )}
                                                    </div>
                                                )}
                                            </div>

                                            {/* Rationale */}
                                            {comment.rationale && (
                                                <div style={{
                                                    padding: '12px 16px',
                                                    backgroundColor: '#FFFBEB',
                                                    border: '1px solid #FDE68A',
                                                    borderRadius: '8px',
                                                    display: 'flex',
                                                    alignItems: 'flex-start',
                                                    gap: '8px',
                                                    marginBottom: '16px',
                                                    flexShrink: 0,
                                                }}>
                                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#D97706" strokeWidth="1.5" style={{ flexShrink: 0, marginTop: '2px' }}>
                                                        <circle cx="12" cy="12" r="10" />
                                                        <path d="M12 16v-4M12 8h.01" />
                                                    </svg>
                                                    <div>
                                                        <div style={{
                                                            fontSize: '11px',
                                                            fontWeight: 600,
                                                            color: '#D97706',
                                                            textTransform: 'uppercase',
                                                            letterSpacing: '0.05em',
                                                            marginBottom: '4px',
                                                        }}>
                                                            Rationale
                                                        </div>
                                                        <p style={{
                                                            fontSize: '13px',
                                                            color: '#92400E',
                                                            fontStyle: 'italic',
                                                            margin: 0,
                                                            lineHeight: '1.5',
                                                        }}>
                                                            {comment.rationale}
                                                        </p>
                                                    </div>
                                                </div>
                                            )}

                                            {/* Spacer */}
                                            <div style={{ flex: 1 }} />

                                            {/* Action Buttons */}
                                            {!comment.posted && isEditable && (
                                                <div style={{
                                                    display: 'flex',
                                                    gap: '10px',
                                                    paddingTop: '16px',
                                                    borderTop: '1px solid rgba(0, 0, 0, 0.08)',
                                                    flexShrink: 0,
                                                }}>
                                                    {comment.status === 'accepted' ? (
                                                        <button
                                                            onClick={() => handleDeclineAndScroll(comment.id)}
                                                            style={{
                                                                flex: 1,
                                                                padding: '12px 20px',
                                                                backgroundColor: '#FFFFFF',
                                                                color: '#666666',
                                                                border: '1px solid #E5E5E5',
                                                                borderRadius: '6px',
                                                                fontSize: '14px',
                                                                fontWeight: 500,
                                                                cursor: 'pointer',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                gap: '8px',
                                                            }}
                                                        >
                                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                                                <path d="M18 6L6 18M6 6l12 12" />
                                                            </svg>
                                                            Remove from Queue
                                                        </button>
                                                    ) : comment.status === 'declined' ? (
                                                        <button
                                                            onClick={() => handleAcceptAndScroll(comment.id)}
                                                            style={{
                                                                flex: 1,
                                                                padding: '12px 20px',
                                                                background: '#7C3AED',
                                                                color: '#FFFFFF',
                                                                border: 'none',
                                                                borderRadius: '6px',
                                                                fontSize: '14px',
                                                                fontWeight: 600,
                                                                cursor: 'pointer',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                gap: '8px',
                                                            }}
                                                        >
                                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                                                <polyline points="20,6 9,17 4,12" />
                                                            </svg>
                                                            Accept
                                                        </button>
                                                    ) : (
                                                        <>
                                                            <button
                                                                onClick={() => handleDeclineAndScroll(comment.id)}
                                                                style={{
                                                                    flex: 1,
                                                                    padding: '12px 20px',
                                                                    backgroundColor: '#FFFFFF',
                                                                    color: '#666666',
                                                                    border: '1px solid #E5E5E5',
                                                                    borderRadius: '6px',
                                                                    fontSize: '14px',
                                                                    fontWeight: 500,
                                                                    cursor: 'pointer',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                    gap: '8px',
                                                                }}
                                                            >
                                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                                                    <path d="M18 6L6 18M6 6l12 12" />
                                                                </svg>
                                                                Skip
                                                            </button>
                                                            <button
                                                                onClick={() => handleAcceptAndScroll(comment.id)}
                                                                style={{
                                                                    flex: 1,
                                                                    padding: '12px 20px',
                                                                    background: '#7C3AED',
                                                                    color: '#FFFFFF',
                                                                    border: 'none',
                                                                    borderRadius: '6px',
                                                                    fontSize: '14px',
                                                                    fontWeight: 600,
                                                                    cursor: 'pointer',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                    gap: '8px',
                                                                }}
                                                            >
                                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                                                    <polyline points="20,6 9,17 4,12" />
                                                                </svg>
                                                                Accept
                                                            </button>
                                                        </>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}

                <style>
                    {`
                        @keyframes spin {
                            0% { transform: rotate(0deg); }
                            100% { transform: rotate(360deg); }
                        }
                    `}
                </style>
            </div>
        </>
    );
};

export default SessionPage;
